import axios from "axios"
import dayjs from "dayjs"
import customParseFormat from 'dayjs/plugin/customParseFormat';
import * as DmsCommon from './dms-common.js';

const PLATFORM = 'Web'; // 'Web' is the Platform used by Win32
const APP_ID = 'dmsWeb';

const INVALID_SESSION_MSG_LIST = ["Invalid SessionID!", "無效的 SessionID", "无效的 SessionID"];

const toHongKongIsoString = (value) => {
  if (!value) return value;

  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Hong_Kong",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);

  const get = (type) => parts.find((p) => p.type === type)?.value;

  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}.000+08:00`;
}

const massageErr = (aErrStr) => {
  return { errMsg: aErrStr, isInvalidSession: (INVALID_SESSION_MSG_LIST.includes(aErrStr)) };
}

const errToStr = (e) => {
  if (!e) return "";
  if (typeof e === "string") {
    return e;
  }
  if (e.errMsg) {
    return e.errMsg;
  }
  if (e.errmsg) {
    return e.errmsg;
  }
  if (e.ErrMsg) {
    return e.ErrMsg;
  }
  if (e.response && e.response.data) {
    const data = e.response.data;
    if (typeof data === "string" && data.trim()) {
      return data;
    }
    if (typeof data === "object") {
      const msg = data.errMsg || data.errmsg || data.ErrMsg || data.detail || data.message;
      if (msg) return msg;
    }
  }
  return e.message || String(e);
}

const checkBlobForError = async (blob) => {
  if (blob && blob instanceof Blob) {
    // If blob is larger than 64KB, it is actual file content, NOT a JSON error.
    // Never call blob.text() on large blobs to avoid V8 max string length (512MB) OOM crashes.
    if (blob.size > 64 * 1024) {
      return null;
    }
    try {
      const text = await blob.text();
      const json = JSON.parse(text);
      if (json && json.success === false) {
        return json.errMsg || json.errmsg || json.ErrMsg || "Download failed.";
      }
      if (json && (json.errMsg || json.errmsg || json.ErrMsg || json.detail || json.message)) {
        return json.errMsg || json.errmsg || json.ErrMsg || json.detail || json.message;
      }
    } catch (err) {
      // Not valid JSON
    }
  }
  return null;
}

const createAxios = (ABaseUrl) => {
  const headers = { 'Content-Type': 'application/json' };
  if (process.env.REACT_APP_FORCE_FROM_EXTERNAL === '1') {
    headers['from-external'] = '1';
  }
  return axios.create({
    baseURL: ABaseUrl,
    timeout: 120 * 1000,
    headers: headers,
  });
}

export class DmsSvcError extends Error {
  constructor(message) {
    super(message);
    this.name = 'DmsSvcError'

    this.isInvalidSession = (INVALID_SESSION_MSG_LIST.includes(message));
    this.errMsg = message;
  }
}




export function logonPromise(ADomain, ALoginID, APassword, ASysID, ASessionID) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vBrowserInfo = DmsCommon.getBrowserInfo();

    const vParam = {
      'domain': ADomain,
      'username': ALoginID,
      'password': APassword,
      'devicePlatform': PLATFORM,
      'sysID': ASysID,
      'sessionID': ASessionID,
      'appID': APP_ID,
      'appVer': process.env.REACT_APP_VER,
      'device': vBrowserInfo.name,
      'deviceVer': vBrowserInfo.version,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "logon",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        if (response.data.success == true) {
          resolve(response.data);
        } else {
          handleReject(response.data.errMsg || 'Login Failed.');
        }

      })
      .catch((e) => {
        handleReject("[logonPromise] Error: " + errToStr(e));
      });


  })
}


export function logonByJwtPromise(AAppID, AJwt) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vBrowserInfo = DmsCommon.getBrowserInfo();

    const vParam = {
      'devicePlatform': PLATFORM,
      'appID': AAppID,
      'dmsWebJwt': AJwt,
      'appVer': process.env.REACT_APP_VER,
      'device': vBrowserInfo.name,
      'deviceVer': vBrowserInfo.version,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "logon",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        if (response.data.success == true) {
          resolve(response.data);
        } else {
          handleReject(response.data.errMsg || 'Login Failed.');
        }

      })
      .catch((e) => {
        handleReject("[logonByJwtPromise] Error: " + errToStr(e));
      });


  })
}



export async function changePwdPromise(ASessionID, ASysID, AUserID, AOldPwd, ANewPwd) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
      'oldPassword': AOldPwd,
      'newPassword': ANewPwd,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "changePwd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.folderID, vData.folderExists];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'changePwdPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[changePwdPromise] Error: " + errToStr(e));
    }
  };


}


export function checkSessionPromise(ASessionID) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vBrowserInfo = DmsCommon.getBrowserInfo();

    const vParam = {
      'devicePlatform': PLATFORM,
      'appID': APP_ID,
      'appVer': process.env.REACT_APP_VER,
      'device': vBrowserInfo.name,
      'deviceVer': vBrowserInfo.version,
      'sessionID': ASessionID
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "logon",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        if (response.data.success == true) {
          resolve(response.data);
        } else {
          handleReject(response.data.errMsg || 'Check session failed.');
        }

      })
      .catch((e) => {
        handleReject("[checkSessionPromise] Error: " + errToStr(e));
      });


  })
}

export function digestDataAfterAuth(aUsedUserID, aUsedDomain, aUsedSessionID, aData) {

  // const vDepts = []
  // aData.depts.map((item) => {
  //   vDepts.push({ "DeptCode": item.sysID, "DeptName": (item.dept || item.deptName), "AdminLevel": item.admin || 0 });
  // })

  const vDepts = aData.depts.map((item) => {
    return {
      "DeptCode": item.sysID,
      "DeptName": (item.dept || item.deptName),
      "AdminLevel": item.admin || 0
    };
  })

  const vUserID = aUsedUserID || aData.loginID;
  const vDomain = aUsedDomain || aData.domain;
  const vSessionID = aData.sessionID;
  const vLastCheckTime = dayjs().toISOString();

  const dispatchValues = {
    userID: vUserID,
    domain: vDomain,
    sessionID: vSessionID,
    lastCheckTime: vLastCheckTime,

    depts: vDepts,
    fullName: aData.fullName,
    lastUpdatePassedSec: 0,
  }

  if (aUsedUserID)
    localStorage.userID = vUserID
  if (aUsedDomain)
    localStorage.domain = vDomain

  localStorage.sessionID = vSessionID

  localStorage.lastCheckTime = vLastCheckTime

  localStorage.depts = JSON.stringify(vDepts)
  localStorage.fullName = dispatchValues.fullName

  return dispatchValues

}

export const getFoldersPromise = (ASessionID, ASysID, ANotByUserPermission) => {


  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'notByUserPermission': ANotByUserPermission || 0,
    };

    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getFolders",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {
        let vData = response.data

        if (vData.success == true) {

          const vFolders = vData.folders.map((f) => {

            return {
              id: f.id,
              ParentID: f.parent,
              FolderName: f.name,
              FolderPath: f.path,
              Grade: f.grade,
              LinkedFolderID: f.lnk,
              UnreadCnt: f.cnt,
              CreatedBy: f.createdBy,
              MobileAccessible: f.mobile || false,
              RagEnabled: f.rag || false,
              IsMonitorFolder: f.monitor || false,
              InternetAccess: f.internet || false,
              UncPath: f.uncPath || "",
            }
          });
          resolve(vFolders);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getFoldersPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[getFoldersPromise] Error: " + errToStr(e));
      });


  })
}

export const logIfLongRunningGetFoldersPromise = async (ASessionID, ASysID, ANotByUserPermission) => {
  let isResolved = false;

  const promise = getFoldersPromise(ASessionID, ASysID, ANotByUserPermission);

  const timeout = 10000;

  // Log a warning after the timeout if the promise is still pending
  setTimeout(() => {
    if (!isResolved) {
      // console.log(`Warning: The promise is taking more than ${timeout / 1000} s!`);
      sendAlertEmailForGetFolderPromise(ASessionID, ASysID);

    }
  }, timeout);

  // When the promise resolves, mark it as completed
  promise.finally(() => {
    isResolved = true;
  });

  return promise; // Return the original promise
}


export const getRFID_ReadersPromise = async (ASessionID, ASysID) => {


  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getRFID_Readers",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.data];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getRFID_Readers Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getRFID_Readers] Error: " + errToStr(e));
    }
  };

}


export async function folderAddPromise(ASessionID, ASysID, AFolderName, AParentID,
  APersonalFolderOwner, AMobileAccess, ARagEnabled, AInternetAccess) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderName': AFolderName || "",
      'parentFolderID': AParentID,
      'personalFolderOwner': APersonalFolderOwner || "",
      'mobile': AMobileAccess,
      'rag': ARagEnabled,
      'internetAccess': AInternetAccess,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.newFolderID, vData.folderExists];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderAddPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderAddPromise] Error: " + errToStr(e));
    }
  };


}

export async function folderUpdatePromise(ASessionID, ASysID, AFolderID, AFolderName, AMobileAccess, ARagEnabled, AInternetAccess) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'folderName': AFolderName,
      'mobile': AMobileAccess,
      'rag': ARagEnabled,
      'internetAccess': AInternetAccess,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderUpdatePromise] Error: " + errToStr(e));
    }
  };


}



// export async function folderUpdateRagPromise(ASessionID, ASysID, AFolderID, ARagEnabled) {

//   try {
//     const vParam = {
//       'sysID': ASysID,
//       'sessionID': ASessionID,
//       'folderID': AFolderID,
//       'rag': ARagEnabled,
//     };

//     let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
//       method: 'POST',
//       url: "folderUpdateRag",
//       headers: { 'Content-Type': 'application/json' },
//       data: vParam,
//     });


//     let vData = response.data

//     if (vData.success == true) {
//       return
//     } else {
//       throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderUpdateRagPromise Failed.');
//     }

//   }
//   catch (e) {
//     if (e instanceof DmsSvcError) {
//       throw e
//     } else {
//       throw new DmsSvcError("[folderUpdateRagPromise] Error: " + errToStr(e));
//     }
//   };


// }


export async function folderDelPromise(ASessionID, ASysID, AFolderID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderDelPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderDelPromise] Error: " + errToStr(e));
    }
  };


}

export async function folderMovePromise(ASessionID, ASysID, AFolderID, ATargetFolderID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'targetFolderID': ATargetFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderMove",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderMovePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderMovePromise] Error: " + errToStr(e));
    }
  };


}


export async function folderCopyPromise(ASessionID, ASysID, AFolderID, ATargetFolderID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'targetFolderID': ATargetFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderCopy",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderCopyPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderCopyPromise] Error: " + errToStr(e));
    }
  };


}

export async function folderRightGetPromise(ASessionID, ASysID, AFolderID, AUserID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'userID': AUserID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderRightGet",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.rights, vData.inherited];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderRightGetPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderRightGetPromise] Error: " + errToStr(e));
    }
  };


}


export async function folderAdminUpdatePromise(ASessionID, ASysID, AFolderID, AIDList) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'userList': AIDList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderAdminUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderAdminUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderAdminUpdatePromise] Error: " + errToStr(e));
    }
  };


}


export async function folderRightUpdatePromise(ASessionID, ASysID, AFolderID, AInherit,
  AResetChild, APermissionList, ARecursive = true) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'inherit': AInherit === true ? 1 : 0,
      'resetChild': AResetChild === true ? 1 : 0,
      'permissionList': APermissionList || [],
      'recursive': ARecursive === true ? 1 : 0,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderRightUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderRightUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderRightUpdatePromise] Error: " + errToStr(e));
    }
  };


}


export async function folderChildRightUpdatePromise(ASessionID, ASysID, AFolderID,
  AResetChild, ARecursive) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'resetChild': AResetChild === true ? 1 : 0,
      'recursive': ARecursive === true ? 1 : 0,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "folderChildRightUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'folderRightUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[folderRightUpdatePromise] Error: " + errToStr(e));
    }
  };


}


// export function getDocsPromise(ASessionID, ASysID, AFolderID, ATagID, APhyLoc,
//   ADocID, AEncodedShareSessionID, AStartRow, AEndRow, AMinColOnly) {


//   return new Promise((resolve, reject) => {

//     const handleReject = (e) => {
//       reject({ errMsg: e, isInvalidSession: (e === INVALID_SESSION_MSG) });
//     }

//     const vParam = {
//       'sessionID': ASessionID,
//       'sysID': ASysID,
//       'folderID': AFolderID,
//       'tagID': ATagID,
//       'phyLoc': APhyLoc,
//       'docIDs': ADocID,
//       'SSID': AEncodedShareSessionID,
//       'withDetail': 0,
//       'minColOnly': (AMinColOnly === true ? 1 : 0),
//       'startRow': AStartRow,
//       'endRow': AEndRow,
//     };


//     createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
//       method: 'POST',
//       url: "getDocs",
//       headers: { 'Content-Type': 'application/json' },
//       data: vParam,
//     })
//       .then(response => {
//         let vData = response.data;

//         dayjs.extend(customParseFormat);


//         if (vData.success == true) {
//           const vDocs = vData.docs.map(item => {
//             return {
//               "SysID": item.sys,
//               "DocID": item.id,
//               "FolderID": item.folderID,
//               "RevNo": item.rev,
//               "DocDate": item.ddate,
//               "DocDateJs": dayjs(item.ddate, "DD/MM/YYYY"),
//               "Subject": item.sbj,
//               "FileName": item.fname,
//               "FileExt": DmsCommon.getFileExtension(item.fname),
//               "FileSize": item.fsize,
//               "FileSizeDisplay": DmsCommon.getReadableFileSizeString(item.fsize),
//               "ReceiveDate": item.rdate,
//               "UpdDate": item.udate,
//               "HasRead": (item.read == '1'),

//               // "ModDate": item.mdate,
//               // "Keyword": item.key,
//               // "Volume": item.vol,
//               // "DocType": item.dtype,
//               // "Comments": item.comments.map(cmt => {
//               //   return {
//               //     "CreateDate": cmt.cdate,
//               //     "CommentText": cmt.cmt,
//               //     "CommentHTML": cmt.cmth,
//               //     "ID": cmt.id,
//               //     "User": cmt.usr
//               //   }
//               // })
//             };
//           })

//           resolve(vDocs);
//         } else {
//           handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getDocsPromise Failed.');
//         }

//       })
//       .catch((e) => {
//         handleReject("[getDocsPromise] Error: " + errToStr(e));
//       });


//   })
// }
const massageDocListData = (aData) => {
  dayjs.extend(customParseFormat);

  const vDocs = aData.map(item => {
    return {
      "SysID": item.sys,
      "DocID": item.id,
      "FolderID": item.folderID,
      "RevNo": item.rev,
      "DocDate": item.ddate,
      "DocDateJs": dayjs(item.ddate, "DD/MM/YYYY"),
      "Subject": item.sbj,
      "FileName": item.fname,
      "FileExt": DmsCommon.getFileExtension(item.fname),
      "FileSize": item.fsize,
      "FileSizeDisplay": DmsCommon.getReadableFileSizeString(item.fsize),
      "ReceiveDate": item.rdate,
      "ReceiveDateJs": dayjs(item.rdate, "DD/MM/YYYY"),
      "UpdDate": item.udate,
      "UpdDateJs": dayjs(item.udate, "DD/MM/YYYY"),
      "HasRead": (item.read == '1'),
      "UpdBy": item.updBy,
      "OurRef": item.ourref,
      "DocType": item.dtype,
      "DocRef": item.docref,
      "Company": item.cpy,
      "Sender": item.sender,
      "Recipient": item.recp,
      "FileLoc": item.floc,
      "Keywords": item.key,
      "CheckOut": item.chkout,
    };
  })

  return vDocs
}

export function getDocsPromise2(ASessionID, ASysID, AFolderID, ATagID, APhyLoc,
  ADocID, AEncodedShareSessionID, AStartRow, AEndRow, AMinColOnly,
  AWithDetail, ASortCol, ASortDesc, ADocTypeOnly) {


  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'folderID': AFolderID,
      'tagID': ATagID,
      'phyLoc': APhyLoc,
      'docIDs': ADocID,
      'SSID': AEncodedShareSessionID,
      'minColOnly': (AMinColOnly === true ? 1 : 0),
      'withDetail': (AWithDetail === true ? 1 : 0),
      'docTypeOnly': (ADocTypeOnly === true ? 1 : 0),
      'startRow': AStartRow,
      'endRow': AEndRow,
      'sortCol': ASortCol,
      'sortDesc': (ASortDesc === true ? 1 : 0),
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getDocs",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {
        let vData = response.data;



        if (vData.success == true) {
          const vDocs = massageDocListData(vData.docs);
          // const vDocs = vData.docs.map(item => {
          //   return {
          //     "SysID": item.sys,
          //     "DocID": item.id,
          //     "FolderID": item.folderID,
          //     "RevNo": item.rev,
          //     "DocDate": item.ddate,
          //     "DocDateJs": dayjs(item.ddate, "DD/MM/YYYY"),
          //     "Subject": item.sbj,
          //     "FileName": item.fname,
          //     "FileExt": DmsCommon.getFileExtension(item.fname),
          //     "FileSize": item.fsize,
          //     "FileSizeDisplay": DmsCommon.getReadableFileSizeString(item.fsize),
          //     "ReceiveDate": item.rdate,
          //     "ReceiveDateJs": dayjs(item.rdate, "DD/MM/YYYY"),
          //     "UpdDate": item.udate,
          //     "UpdDateJs": dayjs(item.udate, "DD/MM/YYYY"),
          //     "HasRead": (item.read == '1'),
          //     "UpdBy": item.updBy,
          //     "OurRef": item.ourref,
          //     "DocType": item.dtype,
          //     "DocRef": item.docref,
          //     "Company": item.cpy,
          //     "Sender": item.sender,
          //     "Recipient": item.recp,
          //     "FileLoc": item.floc,
          //     "Keywords": item.key,
          //   };
          // })

          resolve({
            docs: vDocs,
            recordCnt: vData.recordCnt,
          });
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getDocsPromise2 Failed.');
        }

      })
      .catch((e) => {
        handleReject("[getDocsPromise2] Error: " + errToStr(e));
      });


  })
}

export function markDocAsReadPromise(ASessionID, ASysID, ADocID) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docRead",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {
        let vData = response.data

        if (vData.success == true) {
          resolve();
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'markDocAsReadPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[markDocAsReadPromise] Error: " + errToStr(e));
      });


  })
}


export async function getDocDetailPromise(ASessionID, ASysID, ADocID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getDocDetail",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {

      const vItem = vData.data;

      // Massage date field
      const vDocDetail = {
        ...vItem,
        CreateDate: vItem.CreateDate ? dayjs(vItem.CreateDate) : null,
        DocDate: vItem.DocDate ? dayjs(vItem.DocDate) : null,
        RecDate: vItem.RecDate ? dayjs(vItem.RecDate) : null,
        LastModifyDate: vItem.LastModifyDate ? dayjs(vItem.LastModifyDate) : null,
      }

      return vDocDetail;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getDocDetailPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getDocDetailPromise] Error: " + errToStr(e));
    }
  };


}


export async function docUpdatePromise(ASessionID, ASysID, ADocID, AFolderID, ADocDateISO, ADocTypeCodeList, ASubject,
  ASender, ACompany, ARecipient, ADocRef, AOurRef, AFileLoc, AKeywords, ARFID, ARecDateISO) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'folderID': AFolderID,
      'docDate': ADocDateISO,
      'docTypeCodes': ADocTypeCodeList,
      'subject': ASubject,
      'sender': ASender,
      'cmp': ACompany,
      'recipient': ARecipient,
      'docRef': ADocRef,
      'ourRef': AOurRef,
      'loc': AFileLoc,
      'keywords': AKeywords,
      'RFID': ARFID,
      'recDate': ARecDateISO,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[docUpdatePromise] Error: " + errToStr(e));
    }
  };


}


export async function updateDocDListPromise(ASessionID, ASysID, ADocID, ADistList) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'distList': ADistList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "updateDocDList",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateDocDListPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateDocDListPromise] Error: " + errToStr(e));
    }
  };


}


export async function updateBatchDocDListPromise(ASessionID, ASysID, ADocIDs, ADistList) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docIDs': ADocIDs,
      'distList': ADistList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "updateBatchDocDList",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateBatchDocDListPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateBatchDocDListPromise] Error: " + errToStr(e));
    }
  };


}


export async function imgOCR_Promise(ASessionID, AFileID, ABoundBoxList) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'fileID': AFileID,
      'boundBoxList': ABoundBoxList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "imgOCR",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return vData.boxes
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'imgOCR_Promise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[imgOCR_Promise] Error: " + errToStr(e));
    }
  };


}


export async function dmsFilePrompt_Promise(ASessionID, ASysID, AFile, ARegion = null, AProject = null) {

  try {
    // const vParam = {
    //   'fileID': AFileID,
    //   'fileName': AFileName,
    // };

    const formData = new FormData();
    formData.append("sessionID", ASessionID);
    formData.append("sysID", ASysID);
    formData.append("file", AFile);
    if (ARegion) {
      formData.append("region", ARegion);
    }
    if (AProject) {
      formData.append("project", AProject);
    }

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "dmsFilePrompt",
      headers: { 'Content-Type': 'form-data' },
      data: formData,
    });


    let vData = response.data

    if (vData.success == true) {
      return vData
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'dmsFilePrompt_Promise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[dmsFilePrompt_Promise] Error: " + errToStr(e));
    }
  };


}

export async function aiCompareDocRevisions_Promise(ASessionID, ASysID, ADocID, ARevNo1, ARevNo2, APrompt, AModel, AAllPages) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'docID': ADocID,
      'revNo1': ARevNo1,
      'revNo2': ARevNo2,
      'prompt': APrompt || '',
      'model': AModel || '',
      'allpages': AAllPages !== undefined && AAllPages !== null ? (AAllPages ? 1 : 0) : 1,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "aiCompareDocRevisions",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data;

    if (vData.success === true) {
      return vData;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'aiCompareDocRevisions_Promise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e;
    } else {
      throw new DmsSvcError("[aiCompareDocRevisions_Promise] Error: " + errToStr(e));
    }
  }

}


export async function updateDocPermissionPromise(ASessionID, ASysID, ADocID, AUserIDList) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'userList': AUserIDList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "updateDocPermission",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateDocPermissionPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateDocPermissionPromise] Error: " + errToStr(e));
    }
  };


}


export async function accessLogPromise(ASessionID, ASysID, AAction, AParam) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'action': AAction,
      'param': AParam,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "accessLog",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'accessLogPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateDocPermissionPromise] Error: " + errToStr(e));
    }
  };


}


export async function updateDocDListLogPromise(ASessionID, ASysID, ADocID, ARevNo) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'revNo': ARevNo,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "updateDocDListLog",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateDocDListLogPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateDocDListLogPromise] Error: " + errToStr(e));
    }
  };


}

export async function delDocPromise(ASessionID, ASysID, ADocID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'delDocPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[delDocPromise] Error: " + errToStr(e));
    }
  };


}

export async function docSrcDelPromise(ASessionID, ASysID, ADocID, ASrcNo) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'srcNo': ASrcNo,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docSrcDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docSrcDelPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[docSrcDelPromise] Error: " + errToStr(e));
    }
  };


}


export async function docAttDelPromise(ASessionID, ASysID, ADocID, ASrcNo) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'attNo': ASrcNo,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docAttDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docAttDelPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[docAttDelPromise] Error: " + errToStr(e));
    }
  };


}


export async function getDocTypesPromise(ASessionID, ASysID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getDocTypes",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {

      // Sort by description
      const dataList = vData.data

      dataList.sort((a, b) => a.RefCodeDesc.localeCompare(b.RefCodeDesc))

      return dataList;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getDocTypesPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getDocTypesPromise] Error: " + errToStr(e));
    }
  };


}


export async function deptListGetPromise(ASessionID, ASysID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "deptListGet",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'deptListGetPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[deptListGetPromise] Error: " + errToStr(e));
    }
  };


}


export async function memberAddPromise(ASessionID, ASysID, AUserID, AHashPwd, ADomain, AWinAuth, AName, AEmail, ADeptCode, ADesc, AGrade, ADisabled) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
      'hashPwd': AHashPwd,
      'domain': ADomain,
      'winAuth': (AWinAuth == true ? 1 : 0),
      'name': AName,
      'email': AEmail,
      'deptCode': ADeptCode,
      'desc': ADesc,
      'grade': AGrade,
      'disabled': (ADisabled == true ? 1 : 0),
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "memberAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'memberAddPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[memberAddPromise] Error: " + errToStr(e));
    }
  };


}


export async function memberEditPromise(ASessionID, ASysID, AUserID, AHashPwd, AName, AEmail, ADeptCode, ADesc, AGrade, ADisabled) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
      'hashPwd': AHashPwd,
      'name': AName,
      'email': AEmail,
      'deptCode': ADeptCode,
      'desc': ADesc,
      'grade': AGrade,
      'disabled': (ADisabled == true ? 1 : 0),
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "memberEdit",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'memberEditPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[memberEditPromise] Error: " + errToStr(e));
    }
  };


}


export async function memberDelPromise(ASessionID, ASysID, AUserID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "memberDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'memberDelPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[memberDelPromise] Error: " + errToStr(e));
    }
  };


}



export async function groupAddPromise(ASessionID, ASysID, AGroupName, AGroupDesc, AMembers) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'groupName': AGroupName,
      'groupDesc': AGroupDesc,
      'memberIDs': AMembers,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "groupAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'groupAddPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[groupAddPromise] Error: " + errToStr(e));
    }
  };


}


export async function groupEditPromise(ASessionID, ASysID, AGroupID, AGroupName, AGroupDesc, AMemberIDs) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'groupID': AGroupID,
      'groupName': AGroupName,
      'groupDesc': AGroupDesc,
      'memberIDs': AMemberIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "groupEdit",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'groupEditPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[groupEditPromise] Error: " + errToStr(e));
    }
  };


}


export async function memberUpdateUserGroupsPromise(ASessionID, ASysID, AUserID, AGroupIDs) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
      'grpIDs': AGroupIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "memberUpdateUserGroups",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'memberUpdateUserGroupsPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[memberUpdateUserGroupsPromise] Error: " + errToStr(e));
    }
  };


}


export async function groupDelPromise(ASessionID, ASysID, AGroupID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'groupID': AGroupID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "groupDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'groupDelPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[groupDelPromise] Error: " + errToStr(e));
    }
  };


}




export async function getGroupMembersPromise(ASessionID, ASysID, AGroupID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'groupID': AGroupID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getGroupMembers",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getGroupMembersPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getGroupMembersPromise] Error: " + errToStr(e));
    }
  };


}

export async function addDocTypePromise(ASessionID, ASysID, ADesc) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'desc': ADesc,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "addDocType",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.newRefCode;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'addDocTypePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[addDocTypePromise] Error: " + errToStr(e));
    }
  };


}


export async function editDocTypePromise(ASessionID, ASysID, ACode, ADesc) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'code': ACode,
      'desc': ADesc,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "editDocType",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'editDocTypePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[editDocTypePromise] Error: " + errToStr(e));
    }
  };


}


export async function delDocTypePromise(ASessionID, ASysID, ACode) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'code': ACode,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "delDocType",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'delDocTypePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[delDocTypePromise] Error: " + errToStr(e));
    }
  };


}


export async function getGroupsPromise(ASessionID, ASysID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getGroups",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getGroupsPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getGroupsPromise] Error: " + errToStr(e));
    }
  };

}


export async function getGroupsByUserPromise(ASessionID, ASysID, AUserID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userID': AUserID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getGroupsByUser",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getGroupsByUserPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getGroupsByUserPromise] Error: " + errToStr(e));
    }
  };

}


export async function getMembersPromise(ASessionID, ASysID, AShowDisable) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'showDisable': (AShowDisable === true) ? 1 : 0,
      'domainUserOnly': 1
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getMembers",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getMembersPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getMembersPromise] Error: " + errToStr(e));
    }
  };

}

export async function getEmailListByUsernamePromise(ASessionID, ASysID, AUserIDs, AGroupIDs) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'userList': AUserIDs,
      'groupList': AGroupIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getEmailListByUsername",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getEmailListByUsernamePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getEmailListByUsernamePromise] Error: " + errToStr(e));
    }
  };

}




export async function shareFileByLinkPromise(ASessionID, ASysID, ADocID, APwd, AExpireDays) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docIDs': [ADocID],
      'pwd': APwd,
      'expireDays': AExpireDays,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "shareFileByLink",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.shareLinks;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'shareFileByLinkPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[shareFileByLinkPromise] Error: " + errToStr(e));
    }
  };

}


export async function shareFileByEmailPromise(ASessionID, ASysID, ADocID, AEmails, AMsgToEmail,
  APwd, AExpireDays) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docIDs': [ADocID],
      'emails': AEmails,
      'remark': AMsgToEmail,
      'pwd': APwd,
      'expireDays': AExpireDays,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "shareFileByEmail",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      // Nothing
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'shareFileByEmailPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[shareFileByEmailPromise] Error: " + errToStr(e));
    }
  };

}


export async function sendEmailForShareDmsDocPromise(ASessionID, ASysID, ADocID, AEmails, AMsgToEmail,
  APwd, AExpireDays) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'emails': AEmails,
      'remark': AMsgToEmail,
      'pwd': APwd,
      'expireDays': AExpireDays,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "sendEmailForShareDmsDoc",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      // Nothing
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'sendEmailForShareDmsDocPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[sendEmailForShareDmsDocPromise] Error: " + errToStr(e));
    }
  };

}

export async function getFolderAccessibleUsersPromise(ASessionID, ASysID, AFolderID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getFolderAccessibleUsers",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return [vData.usersIncludeUserGroup, vData.users, vData.groups];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getFolderAccessibleUsersPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getFolderAccessibleUsersPromise] Error: " + errToStr(e));
    }
  };

}



export async function docSearchPromise(ASessionID, ASysID, ASearchFolderID, ASearchStr,
  AUseElastic, ADocDateFrom, ADocDateTo, AAdvSearchCriteria) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': ASearchFolderID,
      'searchStr': ASearchStr,
      'disableElasticSearch': 0,
      'addWildcard': (AUseElastic === true || AUseElastic === 1) ? 1 : 0,
      'advSearch': AAdvSearchCriteria,
      'docDateFrom': ADocDateFrom,
      'docDateTo': ADocDateTo,
      'withDetail': 1,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docSearch",
      timeout: 180000,
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data;


    if (vData.success == true) {
      const vDocs = massageDocListData(vData.docs);
      // const vDocs = vData.docs?.map(item => {
      //   return {
      //     "SysID": item.sys,
      //     "DocID": item.id,
      //     "FolderID": item.folderID,
      //     "RevNo": item.rev,
      //     "DocDate": dayjs(item.ddate).format(DmsCommon.SHORT_DATE_FORMAT),
      //     "DocDateJs": dayjs(item.ddate),
      //     "Subject": item.sbj,
      //     "FileName": item.fname,
      //     "FileExt": DmsCommon.getFileExtension(item.fname),
      //     "FileSize": item.fsize,
      //     "FileSizeDisplay": DmsCommon.getReadableFileSizeString(item.fsize),
      //     "ReceiveDate": dayjs(item.rdate).format(DmsCommon.SHORT_DATE_FORMAT),
      //     "UpdDate": dayjs(item.udate).format(DmsCommon.SHORT_DATE_FORMAT),
      //     "HasRead": (item.read == '1'),

      //     // "DocType": item.dtype,
      //     // "Keyword": item.key,
      //     // "Volume": item.vol,
      //     // "ModDate": dayjs(item.mdate).format(DmsCommon.SHORT_DATE_FORMAT),
      //     // "Comments": item.comments.map(cmt => {
      //     //   return {
      //     //     "CreateDate": cmt.cdate,
      //     //     "CommentText": cmt.cmt,
      //     //     "CommentHTML": cmt.cmth,
      //     //     "ID": cmt.id,
      //     //     "User": cmt.usr
      //     //   }
      //     // })
      //   };
      // }) || []

      return vDocs;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docSearchPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[docSearchPromise] Error: " + errToStr(e));
    }
  };


}


// export async function shareOneTimeFilesByLinkPromise(ASessionID, ASysID, ADocIDs, ABatchZip) {

//   try {
//     const vParam = {
//       'sysID': ASysID,
//       'sessionID': ASessionID,
//       'docIDs': ADocIDs,
//       'batchZip': (ABatchZip === true) ? 1 : 0,
//     };

//     let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
//       method: 'POST',
//       url: "shareOneTimeFilesByLink",
//       headers: { 'Content-Type': 'application/json' },
//       data: vParam,
//     });


//     let vData = response.data

//     if (vData.success == true) {

//       return vData.shareLinks;

//     } else {
//       throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'shareOneTimeFilesByLinkPromise Failed.');
//     }

//   }
//   catch (e) {
//     if (e instanceof DmsSvcError) {
//       throw e
//     } else {
//       throw new DmsSvcError("[shareOneTimeFilesByLinkPromise] Error: " + errToStr(e));
//     }
//   };


// }

export async function searchDomainUsersPromise(ASessionID, ASysID, ASearchStr) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'searchStr': ASearchStr,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "searchDomainUsers",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data



    if (vData.success == true) {

      return vData.data;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'searchDomainUsersPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[searchDomainUsersPromise] Error: " + errToStr(e));
    }
  };


}


export async function docParentUpdatePromise(ASessionID, ASysID, ADocIDs, AFolderID, ADList) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'docIDs': ADocIDs,
      'folderID': AFolderID,
      'distList': ADList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docParentUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {

      return;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docParentUpdatePromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[docParentUpdatePromise] Error: " + errToStr(e));
    }
  };


}

export async function delDocsPromise(ASessionID, ASysID, ADocIDs) {
  for (const vDocID of ADocIDs) {
    await delDocPromise(ASessionID, ASysID, vDocID);
  }

}

export function getDownloadParam(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach) {

  return `sessionID=${ASessionID}&sysID=${ASysID}&docID=${ADocID}&revNo=${ARevNo || ""}&srcNo=${ASrcNo || ""}&attNo=${AAttNo || ""}&chkAttNo=${AChkAttNo || ""}&isAttach=${AIsAttach ? 1 : 0}`
}

export function getDownloadUrl(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach) {

  const vParam = getDownloadParam(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach);
  const vEncodedUrl = DmsCommon.encodeStrForPassing(vParam);
  let vBaseUrl = process.env.REACT_APP_DMS_FILE_DOWNLOAD_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL;
  if (vBaseUrl && !vBaseUrl.endsWith('/')) {
    vBaseUrl += '/';
  }

  return `${vBaseUrl}downloadFile?encode=${vEncodedUrl}`
}

export function getDownloadUrlWithFilename(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach, AFilename) {

  const vParam = getDownloadParam(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach);
  const vParamWithFilename = `${vParam}&filename=${AFilename}`
  const vEncodedUrl = DmsCommon.encodeStrForPassing(vParamWithFilename);
  let vBaseUrl = process.env.REACT_APP_DMS_FILE_DOWNLOAD_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL;
  if (vBaseUrl && !vBaseUrl.endsWith('/')) {
    vBaseUrl += '/';
  }

  return `${vBaseUrl}downloadFile?encode=${vEncodedUrl}`
}

/**
 * Trigger native browser streaming download directly to disk without loading into JavaScript RAM.
 * Supports onStart, onReady, and onError callbacks using the download token cookie notification pattern.
 */
export function downloadFileStream(
  ASessionID,
  ASysID,
  ADocID,
  ARevNo,
  ASrcNo,
  AAttNo,
  AChkAttNo,
  AFilename,
  onStart = null,
  onReady = null,
  onError = null
) {
  const token = Date.now() + '_' + Math.random().toString(36).substring(2, 8);
  const cookieName = `dms_dl_${token}`;
  const errorCookieName = `dms_dl_err_${token}`;

  let downloadUrl = getDownloadUrlWithFilename(
    ASessionID,
    ASysID,
    ADocID,
    ARevNo,
    ASrcNo,
    AAttNo,
    AChkAttNo,
    1,
    AFilename
  );
  downloadUrl += `&downloadToken=${token}`;

  if (onStart) {
    onStart();
  }

  const a = document.createElement('a');
  a.href = downloadUrl;
  a.download = AFilename || '';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);

  // Poll for the cookie set by server once the file is ready & transfer begins
  let attempts = 0;
  const maxAttempts = 300; // 60 seconds (300 * 200ms)
  const checkTimer = setInterval(() => {
    attempts++;
    const cookies = document.cookie || '';

    // Success: Server finished preparation and started streaming
    if (cookies.indexOf(`${cookieName}=`) !== -1) {
      clearInterval(checkTimer);
      document.cookie = `${cookieName}=; Max-Age=0; path=/;`;
      if (onReady) {
        onReady();
      }
      return;
    }

    // Error: Server reported an error
    if (cookies.indexOf(`${errorCookieName}=`) !== -1) {
      clearInterval(checkTimer);
      let errMsg = "Download failed";
      try {
        const match = cookies.match(new RegExp(`${errorCookieName}=([^;]+)`));
        if (match && match[1]) {
          errMsg = decodeURIComponent(match[1].replace(/\+/g, ' '));
        }
      } catch (e) { }
      document.cookie = `${errorCookieName}=; Max-Age=0; path=/;`;
      if (onReady) {
        onReady();
      }
      if (onError) {
        onError(errMsg);
      }
      return;
    }

    // Timeout: Stop polling after maxAttempts
    if (attempts >= maxAttempts) {
      clearInterval(checkTimer);
      if (onReady) {
        onReady();
      }
    }
  }, 200);
}

export function getAnnotatedPdfUrl(ASessionID, ASysID, ADocID, ARevNo1, ARevNo2, ATargetRevNo, ASections) {
  const params = new URLSearchParams();
  params.append('sessionID', ASessionID || '');
  params.append('sysID', ASysID || '');
  params.append('docID', String(ADocID || 0));
  params.append('revNo1', String(ARevNo1 || 0));
  params.append('revNo2', String(ARevNo2 || 0));
  params.append('targetRevNo', String(ATargetRevNo || ARevNo2 || 0));
  if (ASections && Array.isArray(ASections)) {
    params.append('sections', JSON.stringify(ASections));
  }
  return `${process.env.REACT_APP_DMS_SERVICE_URL}aiDownloadAnnotatedPdf?${params.toString()}`;
}


export function getVideoUrl(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach) {

  const vParam = getDownloadParam(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, AChkAttNo, AIsAttach);
  const vEncodedUrl = DmsCommon.encodeStrForPassing(vParam);

  return `/video.html?${vEncodedUrl}`
}

export function downloadPromise(ASessionID, ASysID, ADocID, ARevNo, ASrcNo, AAttNo, ACheckLogAttNo) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'revNo': ARevNo || 0,
      'srcNo': ASrcNo || 0,
      'attNo': AAttNo || 0,
      'chkAttNo': ACheckLogAttNo || 0,
      'isAttach': 1,
    };


    createAxios(process.env.REACT_APP_DMS_FILE_DOWNLOAD_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "downloadFile",
      timeout: 600000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    })
      .then(async response => {
        const err = await checkBlobForError(response.data);
        if (err) {
          handleReject(err);
        } else {
          resolve(response.data);
        }
      })
      .catch(async (e) => {
        if (e.response && e.response.data) {
          const err = await checkBlobForError(e.response.data);
          if (err) {
            handleReject(err);
            return;
          }
        }
        handleReject("[downloadPromise] Error: " + errToStr(e));
      });


  })
}


export async function downloadPrintIndexExcelPromise(ASessionID, ASysID, AFolderID, ADocIDs, AIncludeSubFolder, AIncludeRight, AIncludeComment) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'docIDs': ADocIDs.join(),
      'includeSubFolder': AIncludeSubFolder ? 1 : 0,
      'includeRight': AIncludeRight ? 1 : 0,
      'includeComment': AIncludeComment ? 1 : 0,
      'exportExcel': 1,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "printDocIdx",
      timeout: 60000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    });


    const err = await checkBlobForError(response.data);
    if (err) {
      throw new DmsSvcError(err);
    }

    return response.data;

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      if (e.response && e.response.data) {
        const err = await checkBlobForError(e.response.data);
        if (err) {
          throw new DmsSvcError(err);
        }
      }
      throw new DmsSvcError("[downloadPrintIndexExcelPromise] Error: " + errToStr(e));
    }
  };


}

export function downloadBatchPromise(ASessionID, ASysID, ABatchDocList) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vDocsData = ABatchDocList.map(item => { return { "docID": item.docID, "revNo": item.revNo } });
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'batchZip': 1,
      'batchDocList': vDocsData,
    };



    createAxios(process.env.REACT_APP_DMS_FILE_DOWNLOAD_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "downloadFile",
      timeout: 600000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    })
      .then(async response => {
        const err = await checkBlobForError(response.data);
        if (err) {
          handleReject(err);
        } else {
          resolve(response.data);
        }
      })
      .catch(async (e) => {
        if (e.response && e.response.data) {
          const err = await checkBlobForError(e.response.data);
          if (err) {
            handleReject(err);
            return;
          }
        }
        handleReject("[downloadPromise] Error: " + errToStr(e));
      });


  })
}


export function uploadFilePromise(ASessionID, AFileData, AFileID, APartNum) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    if (AFileData && AFileData.size > DmsCommon.MAX_FILE_SIZE) {
      handleReject('File size exceeds 2GB limit!');
      return;
    }

    const formData = new FormData();

    formData.append("sessionID", ASessionID);
    formData.append("file", AFileData);
    AFileID && formData.append("fileID", AFileID);
    APartNum && formData.append("partNum", APartNum);
    const vUseSharedStorageEnv = process.env.REACT_APP_USE_SHARED_STORAGE;
    if (vUseSharedStorageEnv !== undefined && vUseSharedStorageEnv !== "") {
      const isShared = ["true", "1", "yes", "y"].includes(String(vUseSharedStorageEnv).trim().toLowerCase());
      formData.append("useSharedStorage", isShared);
    } else {
      formData.append("useSharedStorage", true);
    }

    createAxios(process.env.REACT_APP_DMS_FILE_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "FileUpload",
      // url: "UploadFile.aspx",
      timeout: 60000,
      headers: { 'Content-Type': 'form-data' },
      data: formData,
    })
      .then(response => {
        let vData = response.data

        if (vData.success == true) {
          resolve(vData.fileID);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'uploadFilePromise Failed.');
        }
      })
      .catch((e) => {
        handleReject("[uploadFilePromise] Error: " + errToStr(e));
      });


  })
}



export function processFileOnlyPromise(AFileID, APartCount) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'fileID': AFileID,
      'partCount': APartCount,
    };


    createAxios(process.env.REACT_APP_DMS_FILE_SERVICE_URL || process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "processFileOnly",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {
        const vData = response.data;

        if (vData.success == true) {
          resolve();
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'processFileOnlyPromise Failed.');
        }
      })
      .catch((e) => {
        handleReject("[processFileOnlyPromise] Error: " + errToStr(e));
      });


  })
}


export function uploadChunkedFilePromise(ASessionID, AFile, onProgress = null, concurrency = DmsCommon.UPLOAD_CONCURRENCY || 4, onStatusChange = null) {
  return new Promise((resolve, reject) => {
    const totalSize = AFile ? AFile.size : 0;
    if (totalSize > DmsCommon.MAX_FILE_SIZE) {
      reject(massageErr('File size exceeds 2GB limit!'));
      return;
    }
    if (totalSize === 0) {
      resolve("");
      return;
    }

    const vFileID = DmsCommon.CreateFileID();
    const chunkSize = DmsCommon.FILE_CHUNK_SIZE;

    const chunks = [];
    let partNum = 0;
    for (let start = 0; start < totalSize; start += chunkSize) {
      partNum += 1;
      chunks.push({
        partNum,
        chunk: AFile.slice(start, start + chunkSize),
        size: Math.min(chunkSize, totalSize - start)
      });
    }

    const totalParts = partNum;
    let uploadedBytes = 0;
    let nextIndex = 0;
    let isAborted = false;

    const worker = async () => {
      while (nextIndex < chunks.length && !isAborted) {
        const item = chunks[nextIndex++];
        try {
          await uploadFilePromise(ASessionID, item.chunk, vFileID, item.partNum);
          uploadedBytes += item.size;
          if (onProgress) {
            onProgress(Math.min(100, (uploadedBytes * 100) / totalSize), uploadedBytes, totalSize);
          }
        } catch (err) {
          isAborted = true;
          throw err;
        }
      }
    };

    const workerCount = Math.min(concurrency, chunks.length);
    const workers = [];
    for (let i = 0; i < workerCount; i++) {
      workers.push(worker());
    }

    Promise.all(workers)
      .then(async () => {
        if (onStatusChange) {
          onStatusChange("assembling");
        }
        await processFileOnlyPromise(vFileID, totalParts);
        resolve(vFileID);
      })
      .catch((err) => {
        reject(err);
      });
  });
}


export function docAddPromise(ASessionID, ASysID, AFolderID, ASubject,
  ADocDate, ADocType, ASender, ACompany, ARecipient, ADocRef, AOurRef, AFileLocation, AKeywords,
  ARFID, ARecDate, AFileID, AFilename, AFileSize, ATagIDList, ADistList, APermUserList,
  AUseBlankFile) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    if (AFileSize && AFileSize > DmsCommon.MAX_FILE_SIZE) {
      handleReject('File size exceeds 2GB limit!');
      return;
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
      'subject': ASubject,
      'docDate': ADocDate,
      'docTypeCodes': ADocType,
      'sender': ASender,
      'company': ACompany,
      'recipient': ARecipient,
      'docRef': ADocRef,
      'ourRef': AOurRef,
      'fileLocation': AFileLocation,
      'keywords': AKeywords,
      'RFID': ARFID,
      'recDate': ARecDate,
      'fileID': AFileID,
      'fileName': AFilename,
      'fileSize': AFileSize,
      'tagList': ATagIDList.join(),
      'distList': ADistList,
      'permUserList': APermUserList,
      'useBlankFile': AUseBlankFile === true ? 1 : 0,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {
        let vData = response.data;

        if (vData.success === true) {
          resolve(vData.docID);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docAddPromise Failed.');
        }
      })
      .catch((e) => {
        handleReject("[docAddPromise] Error: " + errToStr(e));
      });


  })
}


export function addDocRevPromise(ASessionID, ASysID, ADocID, AFileID, AFilename, AFileSize, ARemark, ADistList) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    if (AFileSize && AFileSize > DmsCommon.MAX_FILE_SIZE) {
      handleReject('File size exceeds 2GB limit!');
      return;
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'fileID': AFileID,
      'fileName': AFilename,
      'fileSize': AFileSize,
      'remark': ARemark,
      'distList': ADistList,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docRevAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        let vData = response.data

        if (vData.success === true) {
          resolve(vData.revNo);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'addDocRevPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[addDocRevPromise] Error: " + errToStr(e));
      });


  })
}


export function docSrcAddPromise(ASessionID, ASysID, ADocID, AFileID, AFilename, AFileSize, ARemark) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    if (AFileSize && AFileSize > DmsCommon.MAX_FILE_SIZE) {
      handleReject('File size exceeds 2GB limit!');
      return;
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'fileID': AFileID,
      'fileName': AFilename,
      'fileSize': AFileSize,
      'remark': ARemark,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docSrcAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        let vData = response.data

        if (vData.success === true) {
          resolve(vData.srcNo);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docSrcAddPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[docSrcAddPromise] Error: " + errToStr(e));
      });


  })
}



export function docAttAddPromise(ASessionID, ASysID, ADocID, AFileID, AFilename, AFileSize, ARemark) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    if (AFileSize && AFileSize > DmsCommon.MAX_FILE_SIZE) {
      handleReject('File size exceeds 2GB limit!');
      return;
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'fileID': AFileID,
      'fileName': AFilename,
      'fileSize': AFileSize,
      'remark': ARemark,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "docAttAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        let vData = response.data

        if (vData.success === true) {
          resolve(vData.attNo);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'docAttAddPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[docAttAddPromise] Error: " + errToStr(e));
      });


  })
}


export function commentAddPromise(ASessionID, ASysID, ADocID, ACommentText, ACommentHtml) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'commentText': ACommentText,
      'commentHtml': ACommentHtml,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "commentAdd",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        let vData = response.data

        if (vData.success === true) {
          resolve(vData.attNo);
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'commentAddPromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[commentAddPromise] Error: " + errToStr(e));
      });


  })
}

export function commentUpdatePromise(ASessionID, ASysID, ADocID, ACmtID, ACommentText, ACommentHtml) {

  return new Promise((resolve, reject) => {

    const handleReject = (e) => {
      reject(massageErr(e));
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'cmtID': ACmtID,
      'commentText': ACommentText,
      'commentHtml': ACommentHtml,
    };


    createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "commentUpdate",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    })
      .then(response => {

        let vData = response.data

        if (vData.success === true) {
          resolve();
        } else {
          handleReject(vData.errMsg || vData.errmsg || vData.ErrMsg || 'commentUpdatePromise Failed.');
        }

      })
      .catch((e) => {
        handleReject("[commentUpdatePromise] Error: " + errToStr(e));
      });


  })
}


export async function shareLinkDelPromise(ASessionID, ASysID, ADocLinkUID) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docLinkUID': ADocLinkUID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "shareLinkDel",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      // Do nothing
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getDocDetailPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[shareLinkDelPromise] Error: " + errToStr(e));
    }
  };


}


export async function getFolderAdminPromise(ASessionID, ASysID, AFolderID) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'folderID': AFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getFolderAdmin",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data;

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getFolderAdminPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getFolderAdminPromise] Error: " + errToStr(e));
    }
  };

}




export async function printFolderListPromise(ASessionID, ASysID, AIncludeRight,
  AShowAuthFolderOnly, AUserID, APrintDT) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'includeRight': AIncludeRight === true ? 1 : 0,
      'showAuthFolderOnly': AShowAuthFolderOnly === true ? 1 : 0,
      'userID': AUserID,
      'printDT': APrintDT,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "printFolderList",
      timeout: 600000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    });

    return response.data;

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[printFolderListPromise] Error: " + errToStr(e));
    }
  };

}



export async function printCheckoutHistPromise(ASessionID, ASysID,
  ACoDeptList, AHandledBy, ARequestDTFrom, ARequestDTTo,
  ASentOutDTFrom, ASentOutDTTo,
  ASentOutReason, AOurRef, AReturnDTFrom, AReturnDTTo,
  ACheckInBy, AReturned, ASelFolderID, APrintDT) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'compList': ACoDeptList,
      'handledBy': AHandledBy,
      requestDateFrom: toHongKongIsoString(ARequestDTFrom),
      requestDateTo: toHongKongIsoString(ARequestDTTo),
      sentOutDateFrom: toHongKongIsoString(ASentOutDTFrom),
      sentOutDateTo: toHongKongIsoString(ASentOutDTTo),
      sentOutReason: ASentOutReason,
      ourRef: AOurRef,
      returnDateFrom: toHongKongIsoString(AReturnDTFrom),
      returnDateTo: toHongKongIsoString(AReturnDTTo),
      'checkInBy': ACheckInBy,
      'returnYet': AReturned, // 1 = returned, 0 = not returned, 2 = all
      'selFolderID': ASelFolderID,
      'printDT': APrintDT,

    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "printCheckoutHist",
      timeout: 600000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    });

    return response.data;

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[printCheckHistPromise] Error: " + errToStr(e));
    }
  };

}

export async function exportCheckoutHistPromise(ASessionID, ASysID,
  ACoDeptList, AHandledBy, ARequestDTFrom, ARequestDTTo,
  ASentOutDTFrom, ASentOutDTTo,
  ASentOutReason, AOurRef, AReturnDTFrom, AReturnDTTo,
  ACheckInBy, AReturned, ASelFolderID, APrintDT) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'compList': ACoDeptList,
      'handledBy': AHandledBy,
      'requestDateFrom': ARequestDTFrom,
      'requestDateTo': ARequestDTTo,
      'sentOutDateFrom': ASentOutDTFrom,
      'sentOutDateTo': ASentOutDTTo,
      'sentOutReason': ASentOutReason,
      'ourRef': AOurRef,
      'returnDateFrom': AReturnDTFrom,
      'returnDateTo': AReturnDTTo,
      'checkInBy': ACheckInBy,
      'returnYet': AReturned, // 1 = returned, 0 = not returned, 2 = all
      'selFolderID': ASelFolderID,
      'printDT': APrintDT,

    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "exportCheckoutHist",
      timeout: 600000,
      headers: { 'Content-Type': 'application/json' },
      responseType: 'blob',
      data: vParam,
    });

    return response.data;

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[exportCheckoutHistPromise] Error: " + errToStr(e));
    }
  };

}


export async function getAdminFolderByUserPromise(ASessionID, ASysID) {

  try {

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getAdminFolderByUser",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data;

    if (vData.success == true) {
      return vData.data;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getAdminFolderByUserPromise Failed.');
    }


  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getAdminFolderByUserPromise] Error: " + errToStr(e));
    }
  };

}


export async function copyRootFolderBetweenDeptPromise(ASrcSysID, AFolderPath, ADestSysID) {

  try {
    const vParam = {
      'srcDept': ASrcSysID,
      'srcFolder': AFolderPath,
      'dstDept': ADestSysID,
      'includeSubfolder': 0,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "copyRootFolderBetweenDept",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    const vData = response.data

    if (vData.success == true) {
      // Do nothing
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'copyRootFolderBetweenDeptPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[copyRootFolderBetweenDeptPromise] Error: " + errToStr(e));
    }
  };


}




export async function sendAlertEmailForGetFolderPromise(ASessionID, ASysID) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "sendGetFolderAlertEmail",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    const vData = response.data

    if (vData.success == true) {
      // Do nothing
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'sendAlertEmailForGetFolderPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[sendAlertEmailForGetFolderPromise] Error: " + errToStr(e));
    }
  };


}


export async function loggingPromise(aMsg, aFullLoginID, aActionID, aSessionID, aIsError) {

  try {
    const vParam = {
      'msg': aMsg,
      'fullLoginID': aFullLoginID,
      'actionID': aActionID,
      'sessionID': aSessionID,
      'isError': aIsError,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "logging",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data



    if (vData.success == true) {

      return vData.data;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'loggingPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[loggingPromise] Error: " + errToStr(e));
    }
  };


}



export async function getRptCheckHistOptionsPromise(aSessionID, aSysID) {

  try {
    const vParam = {
      'sessionID': aSessionID,
      'sysID': aSysID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getRptCheckHistOptions",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {

      return vData.data;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg ||
        'getRptCheckHistOptionsPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getRptCheckHistOptionsPromise] Error: " + errToStr(e));
    }
  };


}



export async function getOptionsForCheckInHistPromise(aSessionID, aSysID) {

  try {
    const vParam = {
      'sessionID': aSessionID,
      'sysID': aSysID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getOptionsForCheckInHist",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {

      return vData.data;

    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg ||
        'getOptionsForCheckInHistPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[getOptionsForCheckInHistPromise] Error: " + errToStr(e));
    }
  };


}



export async function addCheckOutHistPromise(ASessionID, ASysID, ADocID, ARequestBy, ARequestDT,
  ARequestReason, AHandleBy, AHandleDT, ASentOutBy, ASentOutDT, ASentOutReason,
  ASolicitor, ANewFiles) {

  try {
    if (Array.isArray(ANewFiles)) {
      for (const f of ANewFiles) {
        if ((f?.FileSize || f?.fileSize || f?.size || 0) > DmsCommon.MAX_FILE_SIZE) {
          throw new DmsSvcError('File size exceeds 2GB limit!');
        }
      }
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'requestBy': ARequestBy,
      'requestDate': ARequestDT,
      'requestReason': ARequestReason,
      'handleBy': AHandleBy,
      'handleDate': AHandleDT,
      'sentOutBy': ASentOutBy,
      'sentOutDate': ASentOutDT,
      'sentOutReason': ASentOutReason,
      'solicitor': ASolicitor,
      "newFiles": ANewFiles,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "addCheckOutHist",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success == true) {
      return vData.newCheckLogID;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'addCheckOutHistPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[addCheckOutHistPromise] Error: " + errToStr(e));
    }
  };

}


export async function updateCheckOutHistPromise(ASessionID, ASysID, ADocID, ACheckLogID,
  ARequestBy, ARequestDT, ARequestReason,
  AHandleBy, AHandleDT, ASentOutBy, ASentOutDT, ASentOutReason,
  ASolicitor, ANewFiles, ADeletedAttNoList) {

  try {
    if (Array.isArray(ANewFiles)) {
      for (const f of ANewFiles) {
        if ((f?.FileSize || f?.fileSize || f?.size || 0) > DmsCommon.MAX_FILE_SIZE) {
          throw new DmsSvcError('File size exceeds 2GB limit!');
        }
      }
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'checkLogID': ACheckLogID,
      'requestBy': ARequestBy,
      'requestDate': ARequestDT,
      'requestReason': ARequestReason,
      'handleBy': AHandleBy,
      'handleDate': AHandleDT,
      'sentOutBy': ASentOutBy,
      'sentOutDate': ASentOutDT,
      'sentOutReason': ASentOutReason,
      'solicitor': ASolicitor,
      'newFiles': ANewFiles,
      'delAttNoList': ADeletedAttNoList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "updateCheckOutHist",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success !== true) {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateCheckOutHistPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateCheckOutHistPromise] Error: " + errToStr(e));
    }
  };

}


export async function checkInHistPromise(ASessionID, ASysID, ACheckLogID,
  ACheckInBy, AReturnBy, AReturnDT, AReturnRemark, AFileLoc,
  ANewFiles, ADeletedAttNoList) {

  try {
    if (Array.isArray(ANewFiles)) {
      for (const f of ANewFiles) {
        if ((f?.FileSize || f?.fileSize || f?.size || 0) > DmsCommon.MAX_FILE_SIZE) {
          throw new DmsSvcError('File size exceeds 2GB limit!');
        }
      }
    }

    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'checkLogID': ACheckLogID,
      'checkInBy': ACheckInBy,
      'returnBy': AReturnBy,
      'returnDate': AReturnDT,
      'returnRemark': AReturnRemark,
      'fileLoc': AFileLoc,
      'newFiles': ANewFiles,
      'delAttNoList': ADeletedAttNoList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "checkInHist",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success !== true) {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'checkInHistPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[checkInHistPromise] Error: " + errToStr(e));
    }
  };

}


export async function getRequestReasonListPromise(ASessionID, ASysID, ADocID, ACheckLogID,
  ARequestBy, ARequestDT, ARequestReason,
  AHandleBy, AHandleDT, ASentOutBy, ASentOutDT, ASentOutReason,
  ASolicitor, ANewFiles, ADeletedAttNoList) {

  try {
    const vParam = {
      'sysID': ASysID,
      'sessionID': ASessionID,
      'docID': ADocID,
      'checkLogID': ACheckLogID,
      'requestBy': ARequestBy,
      'requestDate': ARequestDT,
      'requestReason': ARequestReason,
      'handleBy': AHandleBy,
      'handleDate': AHandleDT,
      'sentOutBy': ASentOutBy,
      'sentOutDate': ASentOutDT,
      'sentOutReason': ASentOutReason,
      'solicitor': ASolicitor,
      'newFiles': ANewFiles,
      'delAttNoList': ADeletedAttNoList,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getRequestReasonList",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data

    if (vData.success !== true) {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'updateCheckOutHistPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[updateCheckOutHistPromise] Error: " + errToStr(e));
    }
  };

}



export async function ragCompletionsPromise(ASessionID, AConversationID, AMessage, ASysID, AFolderIDs) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'conversationID': AConversationID,
      'message': AMessage,
      'sysID': ASysID,
      'folderIDs': AFolderIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "ragCompletions",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return vData;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'ragCompletionsPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[ragCompletionsPromise] Error: " + errToStr(e));
    }
  };


}



export async function ragCompletionsStreamPromise(ASessionID, AConversationID, AMessage, ASysID, AFolderIDs, aSignal) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'conversationID': AConversationID,
      'message': AMessage,
      'sysID': ASysID,
      'folderIDs': AFolderIDs,
    };

    const baseUrl = process.env.REACT_APP_DMS_SERVICE_URL || "";
    const url = `${baseUrl.replace(/\/$/, "")}/ragCompletionsStream`;

    const headers = {
      "Content-Type": "application/json",
    };
    if (process.env.REACT_APP_FORCE_FROM_EXTERNAL === '1') {
      headers['from-external'] = '1';
    }

    const response = await fetch(url, {
      method: "POST",
      headers: headers,
      body: JSON.stringify(vParam),
      signal: aSignal,
    });

    return response;

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[ragCompletionsStreamPromise] Error: " + errToStr(e));
    }
  };


}

export async function ragInitChatPromise(ASessionID, ASysID, AFolderIDs) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'folderIDs': AFolderIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "ragInitChat",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return vData.conversationID;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'ragInitChatPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[ragInitChatPromise] Error: " + errToStr(e));
    }
  };


}


export async function ragFilesPromise(ASessionID, ASysID, AFolderIDs) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'folderIDs': AFolderIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "ragFiles",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.fileChunking, vData.datasets];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'ragFilesPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[ragFilesPromise] Error: " + errToStr(e));
    }
  };


}


export async function ragResetDatasetPromise(ASessionID, ASysID, AFolderIDs) {

  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'folderIDs': AFolderIDs,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "ragResetDataset",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });


    let vData = response.data

    if (vData.success == true) {
      return [vData.fileChunking, vData.datasets];
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'ragResetDatasetPromise Failed.');
    }

  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e
    } else {
      throw new DmsSvcError("[ragResetDatasetPromise] Error: " + errToStr(e));
    }
  };


}


export async function getMonitorFolderStatusPromise(ASessionID, ASysID, AFolderID) {
  try {
    const vParam = {
      'sessionID': ASessionID,
      'sysID': ASysID,
      'folderID': AFolderID,
    };

    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'POST',
      url: "getMonitorFolderStatus",
      headers: { 'Content-Type': 'application/json' },
      data: vParam,
    });

    let vData = response.data;

    if (vData.success === true) {
      return vData;
    } else {
      throw new DmsSvcError(vData.errMsg || vData.errmsg || vData.ErrMsg || 'getMonitorFolderStatusPromise Failed.');
    }
  }
  catch (e) {
    if (e instanceof DmsSvcError) {
      throw e;
    } else {
      throw new DmsSvcError("[getMonitorFolderStatusPromise] Error: " + errToStr(e));
    }
  }
}

export async function isExternalPromise() {
  try {
    let response = await createAxios(process.env.REACT_APP_DMS_SERVICE_URL)({
      method: 'GET',
      url: "isExternal",
      headers: { 'Content-Type': 'application/json' },
    });
    let vData = response.data;
    if (vData && vData.isExternal === true) {
      return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

